import { Browser } from "@capacitor/browser";
import type { AppleSsoContext } from "Components/Auth/Apple/getAppleSsoContext";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { MS_TIME } from "Utils/TimeUtils";
import {
  getLocalStorageJSON,
  removeLocalStorageItem,
  setLocalStorageJSON,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";

// Browser-redirect Sign in with Apple for the Android shell. Android has no native Apple
// SDK and the web popup cannot hand its result back into a WebView, so the shell opens
// Apple's authorize page in a Chrome Custom Tab. Apple requires response_mode=form_post
// whenever scopes are requested, so the redirect URI is the API's apple-callback endpoint,
// which bounces the result into /auth/apple (AppleRedirectPage) — reached through the
// verified app link, with the moone:// bounce as the shell's fallback.
//
// The code is exchanged with AppleFlow.Redirect: Apple binds it to this exact client ID
// and redirect URI, so the backend must replay them (AppleClientSelector).

const APPLE_AUTHORIZE_URL = "https://appleid.apple.com/auth/authorize";
const APPLE_CALLBACK_PATH = "/api/auth/sso/apple-callback";

// Generous enough for an Apple sign-in including 2FA; short enough that a stale nonce
// cannot be replayed days later.
const STATE_TTL_MS = 10 * MS_TIME.minute;

type AppleSignInState = {
  nonce: string;
  expiresAt: number;
  // Sign-up context captured when the flow STARTED. The return is a fresh page load on
  // which the Redux user is not hydrated yet and the campaign page's affiliate prop is
  // gone, so the guest login and affiliate code would otherwise be lost between the tap
  // and the exchange.
  context: AppleSsoContext;
};

export type ConsumedAppleSignInState = Pick<
  AppleSignInState,
  "nonce" | "context"
>;

const readStoredState = (): AppleSignInState | null => {
  const stored = getLocalStorageJSON<AppleSignInState>(
    LocalStorageKeys.AppleSignInState,
  );
  if (!stored || stored.expiresAt < Date.now()) {
    return null;
  }
  return stored;
};

// Login-CSRF guard: Apple echoes `state` back untouched, so the callback page accepts a
// code only when it carries the nonce this device issued and the nonce is still fresh.
// Stored in localStorage (not sessionStorage) because the return may arrive after the OS
// killed the app process while the user was at Apple — a cold return must still validate.
//
// A fresh, unexpired nonce is REUSED rather than replaced: a second tap (impatience, or
// the Custom Tab not appearing instantly) would otherwise invalidate the first tab's
// return and fail an otherwise good sign-in. The context is refreshed either way.
const issueAppleSignInState = (context: AppleSsoContext): string => {
  const existing = readStoredState();
  const nonce = existing?.nonce ?? crypto.randomUUID();
  setLocalStorageJSON<AppleSignInState>(LocalStorageKeys.AppleSignInState, {
    nonce,
    expiresAt: existing?.expiresAt ?? Date.now() + STATE_TTL_MS,
    context,
  });
  return nonce;
};

// One-shot: reads and clears the stored state so a code can be presented at most once.
// Returns null when nothing was issued, or the issued nonce expired.
export const consumeAppleSignInState = (): ConsumedAppleSignInState | null => {
  const stored = readStoredState();
  removeLocalStorageItem(LocalStorageKeys.AppleSignInState);

  if (!stored) {
    return null;
  }

  return stored;
};

export const openAppleAuthInBrowser = async (
  context: AppleSsoContext,
): Promise<void> => {
  const clientId: string = import.meta.env.VITE_APPLE_CLIENT_ID;
  const apiUrl: string = import.meta.env.VITE_API_URL;

  const params = new URLSearchParams({
    response_type: "code",
    response_mode: "form_post",
    client_id: clientId,
    redirect_uri: `${apiUrl.replace(/\/+$/, "")}${APPLE_CALLBACK_PATH}`,
    scope: "email name",
    state: issueAppleSignInState(context),
  });

  await Browser.open({ url: `${APPLE_AUTHORIZE_URL}?${params.toString()}` });
};
